import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Called when the resize observer fires. See https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 * for in-depth documentation.
 *
 * @callback resizeCallback
 * @param {HTMLElement} element The observed element
 * @param {ResizeObserverEntry[]} entries Array of entries containing the new dimensions of each observed element.
 */

/**
 * Hook to streamline using a ResizeObserver.
 * This hook is not suitable for cases where you want to observe multiple elements
 * or the callback must be run in useEffect rather than useLayoutEffect.
 *
 * Usage:
 *
 *   const myFunction = (element, entries) => {
 *      // do something when element is resized
 *   };
 *
 *   const MyComponent = () => {
 *     const resizeRef = useResizeObserver(myFunction);
 *
 *     return <div ref=resizeRef>hello!</div>;
 *   }
 *
 * @param {resizeCallback} callback The callback to run on resize
 */
const useResizeObserver = (
  callback: (element: HTMLElement, entries: ResizeObserverEntry[]) => void
) => {
  const ref = useRef(null);
  const handleResize = useCallback(
    (element: HTMLElement, entries: ResizeObserverEntry[]) => {
      callback(element, entries);
    },
    [callback]
  );

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(
      (entries: ResizeObserverEntry[]) => {
        if (Array.isArray(entries)) {
          handleResize(ref.current, entries);
        }
      }
    );

    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [handleResize]);

  return ref;
};

export default useResizeObserver;
